import { useCallback, useEffect, useState, useMemo } from 'react'
import { Controller, useForm, FormProvider } from 'react-hook-form'
import {
  Input,
  DSInputType,
  Select,
  Multiselect,
  Info,
  BatchSearch,
  IntervalPicker,
  Tooltip,
  SlimButton,
  MultiselectMenu,
  Typography,
  SelectPill,
  Checkbox,
  Icon
} from 'components'
import { ViewByType } from 'container/ManageOrders'
import * as S from './styles'
import { statusOvKeys } from 'container/Home/components/Banners/interface'
import useParams from '../../../commons/customHooks/useParams'
import { useDispatch, useSelector } from 'store/store'
import { SelectChangeEvent } from '@mui/material'
import { downloadManagementReport, getOvTypes } from 'services/manage_ov'
import { toast } from 'react-toastify'
import { applyCPFCNPJMask } from 'commons/utils/mask'
import ModalReport, { PeriodFormProps } from '../ModalReport'
import { usePermissions } from 'commons/customHooks/useAuth'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import { FormatBodyType, formatRequestBodyByFilters } from 'container/ManageOrders/utils'
import { ActionPerm } from 'components/Structures/Header/constants'
import { UserPreferences } from 'services/login/interfaces'
import { formatDate, getFirstDayOfMonth } from 'commons/utils/date'
import { availableCentersFactories, Channels, Offices } from 'constants/enums'
import { useCommercialStructure } from 'commons/customHooks'
import { getCommercialStructure } from 'store/SharedContext/sharedSlice'

export type FilterProps = {
  onSubmit: (data: FilterDefaultValues) => void
  viewBy: ViewByType
  setViewBy: (value: ViewByType) => void
  disabledsButtons?: string[]
  onChangeFilter(): void
  onClearFilter?: () => void
}

export interface UrlParams {
  codigosCores?: string
  codigoSapCliente?: string
  codigoTipoOrdem?: string
  codigoZona?: string
  codigosCentros?: string
  codigosMateriais?: string
  codigosStatusEstoque?: string
  codigosStatusOrdemVenda?: string
  codigosStatusPedido?: string
  documentoFatura?: string
  mercado?: string
  escritorioVenda?: string
  equipeVenda?: string
  hierarquia?: string
  materialMTO?: string | number
  caracteristicas?: string
  numeroNotaFiscal?: string
  numeroOrdemVenda?: string
  numeroOrdemVendaSAP?: string
  numeroPedidoCliente?: string
  numeroPedidoRascunho?: string
  numeroPedidoEDI?: string
  page?: string
  searchBox?: string
  periodo?: string
}

const defaultOrderStatusCodes = [
  'Aprovado',
  'Concluído',
  'Em aberto',
  'Em análise',
  'Restrito'
]

export const caracteristicasOptions: SelectOptions[] = [
  { title: 'HC', value: 0, viewBy: ['orders', 'ovs'] },
  { title: 'EDI', value: 1, viewBy: ['orders', 'ovs'] },
  { title: 'ND', value: 2, viewBy: ['ovs'] },
  { title: 'Volume Extra', value: 3, viewBy: ['ovs'] }
]

export const defaultValues = (params: UrlParams, viewBy: ViewByType) => {
  const startDate = getFirstDayOfMonth()
  const endDate = formatDate(new Date())
  const defaultInterval = `${startDate} - ${endDate}`

  const preferences = JSON.parse(
    localStorage.getItem('preferences') || '{}'
  ) as UserPreferences
  const {
    CanalDistribuicao: Mercado,
    RegiaoVenda,
    EquipeVenda,
    EscritorioVenda
  } = preferences
  const defaultMercado = Mercado ? [Mercado] : []
  const defaultEscritorio = EscritorioVenda ? [EscritorioVenda] : []
  const defaultEquipe = EquipeVenda ? [EquipeVenda] : []
  const defaultCodigoZona = RegiaoVenda ? [RegiaoVenda] : []

  let formattedDefaultValues = {
    codigosCores: params.codigosCores ?? '',
    codigoSapCliente: params.codigoSapCliente ?? '',
    codigoTipoOrdem: params.codigoTipoOrdem ?? '',
    codigosCentros:
      typeof params.codigosCentros === 'string' ? params.codigosCentros?.split(',') : [],
    codigosMateriais: params.codigosMateriais ?? '',
    codigosStatusEstoque: params.codigosStatusEstoque?.split(',') ?? [],
    codigosStatusOrdemVenda:
      typeof params.codigosStatusOrdemVenda === 'string'
        ? params.codigosStatusOrdemVenda.split(',')
        : [],
    codigosStatusPedido:
      typeof params.codigosStatusPedido === 'string'
        ? params.codigosStatusPedido.split(',')
        : defaultOrderStatusCodes,
    documentoFatura: params.documentoFatura ?? '',
    mercado:
      typeof params.mercado && params.mercado === 'string'
        ? params.mercado.split(',')
        : defaultMercado,
    escritorioVenda:
      typeof params.escritorioVenda === 'string'
        ? params.escritorioVenda.split(',')
        : defaultEscritorio,
    equipeVenda:
      typeof params.equipeVenda === 'string'
        ? params.equipeVenda.split(',')
        : defaultEquipe,
    codigoZona:
      typeof params.codigoZona === 'string'
        ? params.codigoZona?.split(',')
        : defaultCodigoZona,
    hierarquia: params.hierarquia === 'true',
    materialMTO: params.materialMTO ?? 0,
    caracteristicas:
      typeof params.caracteristicas === 'string' ? params.caracteristicas.split(',') : [],
    numeroNotaFiscal: params.numeroNotaFiscal ?? '',
    numeroOrdemVenda: params.numeroOrdemVenda ?? '',
    numeroOrdemVendaSAP: params.numeroOrdemVendaSAP ?? '',
    numeroPedidoCliente: params.numeroPedidoCliente ?? '',
    numeroPedidoRascunho: params.numeroPedidoRascunho ?? '',
    numeroPedidoEDI: params.numeroPedidoEDI ?? '',
    searchBox: params.searchBox ?? '',
    periodo: params.periodo ?? defaultInterval
  }

  if (viewBy === 'orderAnticipation') {
    formattedDefaultValues = {
      ...formattedDefaultValues,
      numeroOrdemVenda: '',
      numeroOrdemVendaSAP: '',
      documentoFatura: '',
      numeroNotaFiscal: '',
      codigosCentros: [],
      codigosStatusOrdemVenda: []
    }
  }

  return formattedDefaultValues
}

export type FilterDefaultValues = ReturnType<typeof defaultValues>

export interface OrderTypesProps {
  ds_tipo_orde: string
  cd_tipo_orde: string
}

type SelectOptions = {
  title: string
  value: string | number
  viewBy?: ViewByType[]
}

export type ReportDowloadOptionsType = 'currentFilter' | 'customPeriod'

export const Filter = ({
  onSubmit,
  viewBy,
  setViewBy,
  disabledsButtons = [],
  onChangeFilter,
  onClearFilter
}: FilterProps) => {
  const dispatch = useDispatch()

  const [loading, setIsLoading] = useState<boolean>(false)
  const [ovsTypes, setOvsTypes] = useState([])
  const [openReportModal, setOpenReportModal] = useState(false)
  const [selectedOption, setSelectedOption] =
    useState<ReportDowloadOptionsType>('currentFilter')
  const [loadingReport, setLoadingReport] = useState(false)
  const [ignoreParams, setIgnoreParams] = useState(false)

  const availableDataType: SelectOptions[] = [
    { title: 'Pedidos', value: 'orders' },
    { title: 'Ordens de venda', value: 'ovs' },
    { title: 'Pré-vendas', value: 'orderAnticipation' }
  ]
  const { clearParamsExcept, getParams } = useParams()
  const materialType: SelectOptions[] = [
    { title: 'Todos', value: 0 },
    { title: 'MTO', value: 1 },
    { title: 'Padrão', value: 2 }
  ]

  const getOvTypesList = async () => {
    setIsLoading(true)
    await getOvTypes()
      .then((response) => {
        const types = response?.data.map((item: OrderTypesProps) => ({
          value: item.cd_tipo_orde,
          title: item.ds_tipo_orde
        }))

        types.unshift({ value: '', title: 'Todos' })
        setOvsTypes(types)
      })
      .catch((error) => toast.error(error.message))
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    getOvTypesList()
  }, [])

  useEffect(() => {
    if (viewBy === 'orderAnticipation') {
      const defaultFields = defaultValues(getParams(), viewBy)
      reset({
        ...defaultFields,
        numeroOrdemVenda: '',
        numeroOrdemVendaSAP: '',
        documentoFatura: '',
        numeroNotaFiscal: '',
        codigosCentros: [],
        codigosStatusOrdemVenda: [],
        caracteristicas: []
      })
    }
  }, [viewBy])

  const { preferences } = useSelector((state) => state.auth)
  const {
    offices: officesCommercialStructure,
    regionals: regionalsCommercialStructure,
    zones: zonesCommercialStructure
  } = useSelector((state) => state.shared)

  const {
    getMarkets,
    getOffices,
    getOfficesByMarkets,
    getRegionalsByOffices,
    getZonesByRegionals
  } = useCommercialStructure()

  // NOTE: Commercial Structure is loaded in Home page, but when user access directly to Management page, it is not loaded
  useEffect(() => {
    // TODO: Check a better way to handle loading of initial commercial structure when force reload - W.I.P
    if (
      !officesCommercialStructure.length &&
      !regionalsCommercialStructure.length &&
      !zonesCommercialStructure.length
    ) {
      dispatch(getCommercialStructure())
    }
  }, [officesCommercialStructure, regionalsCommercialStructure, zonesCommercialStructure])

  const params = getParams()

  const filterSelectedOV = (value: Array<string>) => {
    const number = statusOvKeys.map((item) => Number(item.trim().split('-')[0]))
    const valueFormated = value.map((item) => Number(item.trim().split('-')[0]))

    if (value.length) {
      const returnValue = valueFormated.map((item: number) => {
        const index = number.indexOf(item)
        if (index > -1) {
          return statusOvKeys[index]
        }
        return
      })
      return returnValue
    }
    return []
  }

  const restoreStructure = () => {
    const { mercado, escritorioVenda, equipeVenda, codigoZona } = getParams()

    if (mercado) setValue('mercado', mercado.split(','))
    else {
      setValue(
        'mercado',
        getMarkets().map((market) => market.id)
      )
      return
    }

    if (escritorioVenda) setValue('escritorioVenda', escritorioVenda.split(','))
    else {
      const offices = getOffices()

      setValue(
        'escritorioVenda',
        offices.map((office) => office.id)
      )

      return
    }

    if (equipeVenda) setValue('equipeVenda', equipeVenda.split(','))
    if (codigoZona) setValue('codigoZona', codigoZona.split(','))
  }

  const methods = useForm({
    defaultValues: defaultValues(params, viewBy)
  })

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { isDirty }
  } = methods

  const codSapClie = watch('codigoSapCliente')

  useEffect(() => {
    if (codSapClie === '') setValue('hierarquia', false)
  }, [codSapClie])

  const watchNonPrioritary = watch([
    'codigosCores',
    'codigoSapCliente',
    'codigoTipoOrdem',
    'codigosCentros',
    'codigosMateriais',
    'codigosStatusEstoque',
    'codigosStatusOrdemVenda',
    'codigosStatusPedido',
    'mercado',
    'escritorioVenda',
    'equipeVenda',
    'codigoZona',
    'hierarquia',
    'materialMTO',
    'caracteristicas',
    'searchBox',
    'periodo',
    'numeroPedidoEDI'
  ])

  const draftNumber = watch('numeroPedidoRascunho')
  const orderNumber = watch('numeroOrdemVenda')
  const orderSAPNumber = watch('numeroOrdemVendaSAP')
  const clientOrderNumber = watch('numeroPedidoCliente')
  const invoiceDocument = watch('documentoFatura')
  const invoiceNumber = watch('numeroNotaFiscal')
  const selectedMarkets = watch('mercado') as Channels[]
  const selectedOffices = watch('escritorioVenda')
  const selectedRegions = watch('equipeVenda')
  const selectedZones = watch('codigoZona')
  const numeroPedidoEDI = watch('numeroPedidoEDI')

  const clearPrioritary = () => {
    setValue('numeroPedidoRascunho', '')
    setValue('numeroOrdemVenda', '')
    setValue('numeroOrdemVendaSAP', '')
    setValue('numeroPedidoCliente', '')
    setValue('documentoFatura', '')
    setValue('numeroNotaFiscal', '')
    setValue('numeroPedidoEDI', '')
  }

  const prioritary = [
    draftNumber !== '',
    orderNumber !== '',
    orderSAPNumber !== '',
    clientOrderNumber !== '',
    invoiceDocument !== '',
    invoiceNumber !== '',
    numeroPedidoEDI !== ''
  ]

  const isPrioritaryFilled = prioritary.some((value) => value)

  const markets = useMemo(() => {
    const marketsByOffices = getMarkets()
    return marketsByOffices
  }, [selectedMarkets])

  const offices = useMemo(() => {
    const officesByMarkets = getOfficesByMarkets(selectedMarkets)
    return officesByMarkets
  }, [selectedMarkets, officesCommercialStructure])

  const regionals = useMemo(() => {
    const regionalsByOffices = getRegionalsByOffices(selectedOffices as Offices[])
    return regionalsByOffices
  }, [selectedOffices, regionalsCommercialStructure])

  const zones = useMemo(() => {
    const zonesByRegionals = getZonesByRegionals(selectedRegions)
    return zonesByRegionals
  }, [selectedRegions, zonesCommercialStructure])

  useEffect(() => {
    // NOTE: preferences.CanalDistribuicao (mercado)
    if (!preferences.CanalDistribuicao && !params.mercado && !isPrioritaryFilled) {
      setValue(
        'mercado',
        markets.map((market) => market.id)
      )
    }
  }, [preferences.CanalDistribuicao, params.mercado])

  useEffect(() => {
    if (params.mercado) {
      setValue('mercado', params.mercado.split(','))
    }
  }, [params.mercado])

  useEffect(() => {
    if (ignoreParams || (!preferences.EscritorioVenda && !params.escritorioVenda)) {
      setValue(
        'escritorioVenda',
        offices.map((office) => office.id)
      )
    } else if (selectedOffices.length) {
      setValue('escritorioVenda', selectedOffices)
    }
  }, [selectedMarkets])

  useEffect(() => {
    if (ignoreParams || (!preferences.EquipeVenda && !params.equipeVenda)) {
      setValue(
        'equipeVenda',
        regionals.map((regional) => regional.id)
      )
    } else if (selectedRegions.length) {
      setValue('equipeVenda', selectedRegions)
    }
  }, [selectedOffices])

  useEffect(() => {
    if (ignoreParams || (!preferences.RegiaoVenda && !params.codigoZona)) {
      setValue(
        'codigoZona',
        zones.map((zone) => zone.id)
      )
    } else if (selectedZones.length) {
      setValue('codigoZona', selectedZones)
    }

    if (ignoreParams) {
      setIgnoreParams(false)
    }
  }, [selectedRegions])

  useEffect(() => {
    if (isPrioritaryFilled && isDirty) {
      clearPrioritary()
    }
  }, [watchNonPrioritary])

  useEffect(() => {
    if (!isPrioritaryFilled && isDirty) {
      restoreStructure()
    }
  }, [isPrioritaryFilled])

  useEffect(() => {
    if (draftNumber !== '') {
      reset({
        numeroPedidoRascunho: draftNumber,
        numeroOrdemVenda: '',
        numeroOrdemVendaSAP: '',
        numeroPedidoCliente: '',
        numeroPedidoEDI: '',
        documentoFatura: '',
        numeroNotaFiscal: '',
        codigosCores: '',
        codigoSapCliente: '',
        codigoTipoOrdem: '',
        codigoZona: [],
        codigosCentros: [],
        codigosMateriais: '',
        codigosStatusEstoque: [],
        codigosStatusOrdemVenda: [],
        codigosStatusPedido: defaultOrderStatusCodes,
        mercado: [],
        escritorioVenda: [],
        equipeVenda: [],
        hierarquia: false,
        materialMTO: 0,
        caracteristicas: [],
        searchBox: ''
      })
    }
  }, [draftNumber])

  useEffect(() => {
    if (orderNumber !== '') {
      reset({
        numeroPedidoRascunho: '',
        numeroOrdemVenda: orderNumber,
        numeroOrdemVendaSAP: '',
        numeroPedidoCliente: '',
        numeroPedidoEDI: '',
        documentoFatura: '',
        numeroNotaFiscal: '',
        codigosCores: '',
        codigoSapCliente: '',
        codigoTipoOrdem: '',
        codigoZona: [],
        codigosCentros: [],
        codigosMateriais: '',
        codigosStatusEstoque: [],
        codigosStatusOrdemVenda: [],
        codigosStatusPedido: defaultOrderStatusCodes,
        mercado: [],
        escritorioVenda: [],
        equipeVenda: [],
        hierarquia: false,
        materialMTO: 0,
        caracteristicas: [],
        searchBox: ''
      })
    }
  }, [orderNumber])

  useEffect(() => {
    if (orderSAPNumber !== '') {
      reset({
        numeroPedidoRascunho: '',
        numeroOrdemVenda: '',
        numeroOrdemVendaSAP: orderSAPNumber,
        numeroPedidoCliente: '',
        numeroPedidoEDI: '',
        documentoFatura: '',
        numeroNotaFiscal: '',
        codigosCores: '',
        codigoSapCliente: '',
        codigoTipoOrdem: '',
        codigoZona: [],
        codigosCentros: [],
        codigosMateriais: '',
        codigosStatusEstoque: [],
        codigosStatusOrdemVenda: [],
        codigosStatusPedido: defaultOrderStatusCodes,
        mercado: [],
        escritorioVenda: [],
        equipeVenda: [],
        hierarquia: false,
        materialMTO: 0,
        caracteristicas: [],
        searchBox: ''
      })
    }
  }, [orderSAPNumber])

  useEffect(() => {
    if (clientOrderNumber !== '') {
      reset({
        numeroPedidoRascunho: '',
        numeroOrdemVenda: '',
        numeroOrdemVendaSAP: '',
        numeroPedidoCliente: clientOrderNumber,
        numeroPedidoEDI: '',
        documentoFatura: '',
        numeroNotaFiscal: '',
        codigosCores: '',
        codigoSapCliente: '',
        codigoTipoOrdem: '',
        codigoZona: [],
        codigosCentros: [],
        codigosMateriais: '',
        codigosStatusEstoque: [],
        codigosStatusOrdemVenda: [],
        codigosStatusPedido: defaultOrderStatusCodes,
        mercado: [],
        escritorioVenda: [],
        equipeVenda: [],
        hierarquia: false,
        materialMTO: 0,
        caracteristicas: [],
        searchBox: ''
      })
    }
  }, [clientOrderNumber])

  useEffect(() => {
    if (invoiceDocument !== '') {
      reset({
        numeroPedidoRascunho: '',
        numeroOrdemVenda: '',
        numeroOrdemVendaSAP: '',
        numeroPedidoCliente: '',
        numeroPedidoEDI: '',
        documentoFatura: invoiceDocument,
        numeroNotaFiscal: '',
        codigosCores: '',
        codigoSapCliente: '',
        codigoTipoOrdem: '',
        codigoZona: [],
        codigosCentros: [],
        codigosMateriais: '',
        codigosStatusEstoque: [],
        codigosStatusOrdemVenda: [],
        codigosStatusPedido: defaultOrderStatusCodes,
        mercado: [],
        escritorioVenda: [],
        equipeVenda: [],
        hierarquia: false,
        materialMTO: 0,
        caracteristicas: [],
        searchBox: ''
      })
    }
  }, [invoiceDocument])

  useEffect(() => {
    if (invoiceNumber !== '') {
      reset({
        numeroPedidoRascunho: '',
        numeroOrdemVenda: '',
        numeroOrdemVendaSAP: '',
        numeroPedidoCliente: '',
        numeroPedidoEDI: '',
        documentoFatura: '',
        numeroNotaFiscal: invoiceNumber,
        codigosCores: '',
        codigoSapCliente: '',
        codigoTipoOrdem: '',
        codigoZona: [],
        codigosCentros: [],
        codigosMateriais: '',
        codigosStatusEstoque: [],
        codigosStatusOrdemVenda: [],
        codigosStatusPedido: defaultOrderStatusCodes,
        mercado: [],
        escritorioVenda: [],
        equipeVenda: [],
        hierarquia: false,
        materialMTO: 0,
        caracteristicas: [],
        searchBox: ''
      })
    }
  }, [invoiceNumber])

  useEffect(() => {
    if (numeroPedidoEDI !== '') {
      reset({
        numeroPedidoRascunho: '',
        numeroOrdemVenda: '',
        numeroOrdemVendaSAP: '',
        numeroPedidoCliente: '',
        numeroPedidoEDI: numeroPedidoEDI,
        documentoFatura: '',
        numeroNotaFiscal: '',
        codigosCores: '',
        codigoSapCliente: '',
        codigoTipoOrdem: '',
        codigoZona: [],
        codigosCentros: [],
        codigosMateriais: '',
        codigosStatusEstoque: [],
        codigosStatusOrdemVenda: [],
        codigosStatusPedido: defaultOrderStatusCodes,
        mercado: [],
        escritorioVenda: [],
        equipeVenda: [],
        hierarquia: false,
        materialMTO: 0,
        caracteristicas: [],
        searchBox: ''
      })
    }
  }, [numeroPedidoEDI])

  const labelField = (label: string, name: string, disableLabel?: boolean) => (
    <S.LabelFilter disabled={disabledsButtons.includes(name) || disableLabel}>
      {label}
      {disabledsButtons.includes(name) && (
        <Info>Campo indisponível. Em breve mais informações.</Info>
      )}
    </S.LabelFilter>
  )

  const handleClearFilter = () => {
    clearParamsExcept(['page'])
    reset(defaultValues({}, viewBy))
    restoreStructure()
    onClearFilter ? onClearFilter() : onChangeFilter()
  }

  const isDisabledFilter = (buttonName: string) =>
    disabledsButtons.includes(buttonName) || viewBy === 'orderAnticipation'

  const getSearchParams = (): FormatBodyType => {
    const params = getParams()

    return {
      ...defaultValues,
      ...params
    }
  }

  const handleIgnoreParams = () => {
    setIgnoreParams(true)
  }

  const { verifyPermission } = usePermissions()
  function shouldDisable(action: ActionPerm) {
    return !verifyPermission(SCREENS.MANAGE, action)
  }

  const handleDownloadReport = (data: PeriodFormProps) => {
    let payload = {}
    if (selectedOption === 'currentFilter') {
      payload = formatRequestBodyByFilters(getSearchParams(), false, viewBy, true)
    } else {
      payload = {
        ...formatRequestBodyByFilters(getSearchParams(), false, viewBy, true),
        dataInicial: new Date(data.initialQuota).toISOString(),
        dataFinal: new Date(data.finalQuota).toISOString()
      }
    }

    getReport(payload)
  }

  const getReport = useCallback(
    async (data) => {
      try {
        setLoadingReport(true)
        const response = await downloadManagementReport(data, viewBy)
        // TODO: Replace with utils/downlodFile({file, fileName, fileType})
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        const reportTitle =
          viewBy !== 'orderAnticipation'
            ? 'relatorio_matriz_ov.xlsx'
            : 'pedido_antecipado.xlsx'
        link.setAttribute('download', reportTitle)
        link.click()
        toast.success('Relatório baixado com sucesso!')
      } catch (error) {
        toast.error('Não foi possível baixar o relatório. Tente novamente mais tarde.')
      } finally {
        setLoadingReport(false)
      }
    },
    [selectedOption]
  )

  const getFilteredCaracteristicas = () => {
    return caracteristicasOptions
      .filter((item) => item.viewBy?.includes(viewBy))
      .map((item) => item.title)
  }

  return (
    <>
      <S.Container onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...methods}>
          <S.WrapperHeader>
            <S.HeaderContainer>
              <S.HeaderItem>
                <S.WrapTitleWithButton>
                  <h1>Gerenciamento</h1>
                  <Tooltip title="Baixar relatório" arrow>
                    <div>
                      <SlimButton
                        type="button"
                        variant="secondary"
                        icon={<Icon icon="download" size="15" />}
                        disabled={shouldDisable(ACTIONS_SCREENS.DOWNLOAD)}
                        onClick={() => setOpenReportModal(true)}
                      />
                    </div>
                  </Tooltip>
                </S.WrapTitleWithButton>

                <S.SelectContainer>
                  <Typography design="hav-default-03-text">Exibindo lista de </Typography>
                  <SelectPill
                    name="viewBy"
                    value={viewBy}
                    disabled={disabledsButtons.includes('viewBy')}
                    onChange={(e: SelectChangeEvent<unknown>) => {
                      setViewBy(e.target.value as unknown as ViewByType)
                    }}
                    options={availableDataType}
                  />
                  <Typography design="hav-default-03-text">com</Typography>
                  <Controller
                    name="periodo"
                    control={control}
                    render={({ field }) => (
                      <IntervalPicker
                        disabled={isPrioritaryFilled}
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </S.SelectContainer>
              </S.HeaderItem>
              <S.StructureInputs>
                <S.SelectorWrapper>
                  <Controller
                    name="mercado"
                    control={control}
                    render={({ field }) => (
                      <MultiselectMenu
                        label="Mercado"
                        selectedOptions={
                          Array.isArray(field.value) ? field.value : [field.value]
                        }
                        onChange={(value) => {
                          handleIgnoreParams()
                          field.onChange(value)
                        }}
                        optionsList={markets.map((market) => ({
                          label: market.description,
                          value: market.id
                        }))}
                        required={!isPrioritaryFilled}
                        disabled={isPrioritaryFilled}
                        clearAction={() => {
                          handleIgnoreParams()
                          setValue('mercado', [])
                        }}
                        checkAllAction={() => {
                          handleIgnoreParams()
                          setValue(
                            'mercado',
                            markets.map((item) => item.id)
                          )
                        }}
                      />
                    )}
                  />
                </S.SelectorWrapper>
                <S.SelectorWrapper>
                  <Controller
                    name="escritorioVenda"
                    control={control}
                    render={({ field }) => (
                      <MultiselectMenu
                        label="Escritório"
                        selectedOptions={
                          Array.isArray(field.value) ? field.value : [field.value]
                        }
                        onChange={(value) => {
                          handleIgnoreParams()
                          field.onChange(value)
                        }}
                        optionsList={offices.map((office) => ({
                          label: `${office.id} - ${office.description}`,
                          value: office.id
                        }))}
                        required={!isPrioritaryFilled}
                        disabled={!selectedMarkets.length || isPrioritaryFilled}
                        clearAction={() => {
                          handleIgnoreParams()
                          setValue('escritorioVenda', [])
                        }}
                        checkAllAction={() => {
                          handleIgnoreParams()
                          setValue(
                            'escritorioVenda',
                            offices.map((office) => office.id)
                          )
                        }}
                      />
                    )}
                  />
                </S.SelectorWrapper>
                <S.SelectorWrapper>
                  <Controller
                    name="equipeVenda"
                    control={control}
                    render={({ field }) => (
                      <MultiselectMenu
                        label="Regional"
                        selectedOptions={field.value}
                        onChange={(value) => {
                          handleIgnoreParams()
                          field.onChange(value)
                        }}
                        optionsList={regionals.map((regional) => ({
                          label: `${regional.id} - ${regional.description}`,
                          value: regional.id
                        }))}
                        disabled={!regionals.length || isPrioritaryFilled}
                        required={!isPrioritaryFilled}
                        clearAction={() => {
                          handleIgnoreParams()
                          setValue('equipeVenda', [])
                        }}
                        checkAllAction={() => {
                          handleIgnoreParams()
                          setValue(
                            'equipeVenda',
                            regionals.map((regional) => regional.id)
                          )
                        }}
                      />
                    )}
                  />
                </S.SelectorWrapper>
                <S.SelectorWrapper>
                  <Controller
                    name="codigoZona"
                    control={control}
                    render={({ field }) => (
                      <MultiselectMenu
                        label="Zona"
                        selectedOptions={field.value}
                        onChange={(value) => {
                          field.onChange(value)
                        }}
                        optionsList={zones.map((zone) => ({
                          label: `${zone.id} - ${zone.description}`,
                          value: zone.id
                        }))}
                        hasSearch={zones.length > 10}
                        disabled={!zones.length || isPrioritaryFilled}
                        required={!isPrioritaryFilled}
                        clearAction={() => {
                          setValue('codigoZona', [])
                        }}
                        checkAllAction={() => {
                          setValue(
                            'codigoZona',
                            zones.map((zone) => zone.id)
                          )
                        }}
                      />
                    )}
                  />
                </S.SelectorWrapper>
              </S.StructureInputs>
            </S.HeaderContainer>

            <S.HorDivider />
          </S.WrapperHeader>

          <S.FilterContent>
            <S.FormField>
              {labelField('Pedido portal', 'numeroPedidoRascunho')}
              <Controller
                name="numeroPedidoRascunho"
                control={control}
                render={({ field }) => (
                  <BatchSearch
                    placeHolder="ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ"
                    height={22}
                    onClear={() => {
                      reset({ ...getValues(), numeroPedidoRascunho: '' })
                      restoreStructure()
                    }}
                    disabled={disabledsButtons.includes('numeroPedidoRascunho')}
                    {...field}
                  />
                )}
              />
            </S.FormField>
            {viewBy !== 'orderAnticipation' && (
              <S.FormField>
                {labelField('Nº OV Portal', 'numeroOrdemVenda')}
                <Controller
                  name="numeroOrdemVenda"
                  control={control}
                  render={({ field }) => (
                    <BatchSearch
                      placeHolder="ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ"
                      height={22}
                      onClear={() => {
                        reset({ ...getValues(), numeroOrdemVenda: '' })
                        restoreStructure()
                      }}
                      disabled={isDisabledFilter('numeroOrdemVenda')}
                      {...field}
                    />
                  )}
                />
              </S.FormField>
            )}
            {viewBy !== 'orderAnticipation' && (
              <S.FormField>
                {labelField('Nº OV SAP', 'numeroOrdemVendaSAP')}
                <Controller
                  name="numeroOrdemVendaSAP"
                  control={control}
                  render={({ field }) => (
                    <BatchSearch
                      placeHolder="ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ"
                      height={22}
                      onClear={() => {
                        reset({ ...getValues(), numeroOrdemVendaSAP: '' })
                        restoreStructure()
                      }}
                      disabled={isDisabledFilter('numeroOrdemVendaSAP')}
                      {...field}
                    />
                  )}
                />
              </S.FormField>
            )}
            <S.FormField>
              {labelField('CPF/CNPJ', 'searchBox')}
              <Controller
                name="searchBox"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    disabled={disabledsButtons.includes('searchBox')}
                    design={DSInputType.SEARCH_INPUT}
                    textUppercase
                    height={22}
                    maxLength={18}
                    placeholder="ˍ ˍ.ˍ ˍ ˍ.ˍ ˍ ˍ ˍ/ˍ ˍ ˍ ˍ-ˍˍ"
                    onChange={(value) => {
                      field.onChange(applyCPFCNPJMask(value.target.value))
                    }}
                  />
                )}
              />
            </S.FormField>
            <S.FormField>
              {labelField('Código SAP', 'codigoSapCliente')}
              <S.ClientSapWrapper>
                <Controller
                  name="codigoSapCliente"
                  control={control}
                  render={({ field }) => (
                    <BatchSearch
                      placeHolder="ˍ ˍ ˍ ˍ ˍ ˍ ˍ"
                      height={22}
                      onClear={() => reset({ ...getValues(), codigoSapCliente: '' })}
                      disabled={disabledsButtons.includes('codigoSapCliente')}
                      {...field}
                    />
                  )}
                />
                <S.WrapHierarchyField>
                  <Controller
                    name="hierarquia"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        label="Hierarquia"
                        size={'small'}
                        checked={field.value}
                        disabled={!codSapClie}
                      />
                    )}
                  />
                </S.WrapHierarchyField>
              </S.ClientSapWrapper>
            </S.FormField>
            <S.FormField>
              {labelField('Pedido cliente', 'numeroPedidoCliente')}
              <Controller
                name="numeroPedidoCliente"
                control={control}
                render={({ field }) => (
                  <BatchSearch
                    placeHolder="ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ "
                    height={22}
                    onClear={() => {
                      reset({ ...getValues(), numeroPedidoCliente: '' })
                      restoreStructure()
                    }}
                    disabled={disabledsButtons.includes('numeroPedidoCliente')}
                    {...field}
                  />
                )}
              />
            </S.FormField>
            <S.FormField>
              {labelField('Material', 'codigosMateriais')}
              <Controller
                name="codigosMateriais"
                control={control}
                render={({ field }) => (
                  <BatchSearch
                    placeHolder="Buscar por códigos"
                    height={22}
                    onClear={() => reset({ ...getValues(), codigosMateriais: '' })}
                    disabled={disabledsButtons.includes('codigosMateriais')}
                    {...field}
                  />
                )}
              />
            </S.FormField>
            <S.FormField>
              {labelField('Cor', 'codigosCores')}
              <Controller
                name="codigosCores"
                control={control}
                render={({ field }) => (
                  <BatchSearch
                    {...field}
                    placeHolder="Buscar por códigos"
                    height={22}
                    onClear={() => reset({ ...getValues(), codigosCores: '' })}
                    disabled={disabledsButtons.includes('codigosCores')}
                  />
                )}
              />
            </S.FormField>
            <S.FormField>
              {labelField('Fábrica', 'codigosCentros')}
              <Controller
                name="codigosCentros"
                control={control}
                render={({ field }) => {
                  return (
                    <Multiselect
                      optionsList={availableCentersFactories}
                      height={22}
                      selectedOptions={field.value}
                      disabled={disabledsButtons.includes('codigosCentros')}
                      onChange={(value) => field.onChange(value)}
                    />
                  )
                }}
              />
            </S.FormField>
            <S.FormField>
              {labelField('Tipo de OV', 'codigoTipoOrdem')}
              {loading ? (
                <Controller
                  name="codigoTipoOrdem"
                  control={control}
                  render={({ field }) => (
                    <Select
                      name="loadingPlaceholder"
                      value={field.name}
                      height={22}
                      defaultValue=""
                      disabled={true}
                      options={[]}
                    />
                  )}
                />
              ) : (
                <Controller
                  name="codigoTipoOrdem"
                  control={control}
                  render={({ field }) => (
                    <Select
                      name="codigoTipoOrdem"
                      value={field.value}
                      height={22}
                      defaultValue=""
                      disabled={loading}
                      onChange={field.onChange}
                      options={ovsTypes}
                    />
                  )}
                />
              )}
            </S.FormField>
            {viewBy === 'orders' && (
              <S.FormField>
                {labelField('Status', 'codigosStatusPedido', viewBy !== 'orders')}
                <Controller
                  name="codigosStatusPedido"
                  control={control}
                  render={({ field }) => {
                    return (
                      <Multiselect
                        height={22}
                        optionsList={defaultOrderStatusCodes}
                        disabled={
                          viewBy !== 'orders' ||
                          disabledsButtons.includes('codigosStatusPedido')
                        }
                        selectedOptions={field.value}
                        onChange={(value) => field.onChange(value)}
                      />
                    )
                  }}
                />
              </S.FormField>
            )}
            {viewBy === 'ovs' && (
              <S.FormField>
                {labelField('Status OV', 'codigosStatusOrdemVenda')}
                <Controller
                  name="codigosStatusOrdemVenda"
                  control={control}
                  render={({ field }) => {
                    const inputValue = filterSelectedOV(field.value)
                    return (
                      <Multiselect
                        height={22}
                        optionsList={statusOvKeys}
                        disabled={isDisabledFilter('codigosStatusOrdemVenda')}
                        selectedOptions={JSON.parse(JSON.stringify(inputValue))}
                        onChange={(value) => field.onChange(value)}
                      />
                    )
                  }}
                />
              </S.FormField>
            )}
            <S.FormField>
              {labelField('Tipo de pedido', 'materialMTO')}
              <Controller
                name="materialMTO"
                control={control}
                render={({ field }) => (
                  <Select
                    name="materialMTO"
                    value={field.value}
                    defaultValue={0}
                    height={22}
                    disabled={disabledsButtons.includes('materialMTO')}
                    onChange={(value) => {
                      field.onChange(value)
                    }}
                    options={materialType}
                  />
                )}
              />
            </S.FormField>
            {viewBy === 'ovs' && (
              <S.FormField>
                {labelField('NF', 'numeroNotaFiscal')}
                <Controller
                  name="numeroNotaFiscal"
                  control={control}
                  render={({ field }) => (
                    <BatchSearch
                      placeHolder="___ . ___ . ___"
                      height={22}
                      onClear={() => {
                        reset({ ...getValues(), numeroNotaFiscal: '' })
                        restoreStructure()
                      }}
                      disabled={isDisabledFilter('numeroNotaFiscal')}
                      {...field}
                    />
                  )}
                />
              </S.FormField>
            )}
            {viewBy === 'ovs' && (
              <S.FormField>
                {labelField('Doc. Fatura', 'documentoFatura')}
                <Controller
                  name="documentoFatura"
                  control={control}
                  render={({ field }) => (
                    <BatchSearch
                      placeHolder="___ . ___ . ___"
                      height={22}
                      onClear={() => {
                        reset({ ...getValues(), documentoFatura: '' })
                        restoreStructure()
                      }}
                      disabled={isDisabledFilter('documentoFatura')}
                      {...field}
                    />
                  )}
                />
              </S.FormField>
            )}
            {['ovs', 'orders'].includes(viewBy) && (
              <S.FormField>
                {labelField('Nº EDI', 'numeroPedidoEDI')}
                <Controller
                  name="numeroPedidoEDI"
                  control={control}
                  render={({ field }) => (
                    <BatchSearch
                      placeHolder="ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ ˍ"
                      height={22}
                      onClear={() => {
                        reset({ ...getValues(), numeroPedidoEDI: '' })
                        restoreStructure()
                      }}
                      disabled={disabledsButtons.includes('numeroPedidoEDI')}
                      {...field}
                    />
                  )}
                />
              </S.FormField>
            )}
            <S.FormField flex>
              <Controller
                name="caracteristicas"
                control={control}
                render={({ field }) => {
                  return (
                    <>
                      {getFilteredCaracteristicas().map((option) => (
                        <Checkbox
                          key={option}
                          size="small"
                          checked={field.value.includes(option)}
                          onChange={(e) => {
                            const newOptions = e.target.checked
                              ? [...field.value, option]
                              : field.value.filter((item) => item !== option)
                            field.onChange(newOptions)
                          }}
                          label={option}
                          bold
                        />
                      ))}
                    </>
                  )
                }}
              />
            </S.FormField>

            <S.FormField lastItem>
              <div />
              <S.ClearFiltersWrapper>
                <SlimButton
                  onClick={handleClearFilter}
                  type="button"
                  icon={<Icon icon="clear-all" size="20" />}
                  variant="secondary"
                />
                <SlimButton
                  type="submit"
                  variant="primary"
                  content="Buscar"
                  height={22}
                  disabled={
                    !isPrioritaryFilled &&
                    (!selectedMarkets.length ||
                      !selectedOffices.length ||
                      !selectedRegions.length ||
                      !selectedZones.length)
                  }
                />
              </S.ClearFiltersWrapper>
            </S.FormField>
          </S.FilterContent>
        </FormProvider>
      </S.Container>
      <ModalReport
        open={openReportModal}
        setOpen={setOpenReportModal}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        onSubmit={handleDownloadReport}
        isLoading={loadingReport}
      />
    </>
  )
}
